import {
  BoxOfferInvitationStatus,
  BoxOfferRuleSchema,
  BoxOfferType,
  OfferAutonomousPurchase,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { UserDtoSchema } from "@modules/profile/userProfile/userDto"
import { ProgramType } from "@modules/programs/types/programTypes"
import dayjs from "dayjs"
import { z } from "zod"

export enum StatusFilter {
  ALL = "ALL",
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
}

export const IsoDurationSchema = z
  .string()
  .refine((val) => dayjs.duration(val), {
    message: "Invalid ISO 8601 duration format",
  })
  .meta({ id: "IsoDurationSchema" })

export const BoxOfferInvitationCouponSchema = z
  .object({
    type: z.enum(CouponType),
    value: z.number(),
    durationInMonths: z.number().nullable(),
  })
  .meta({ id: "BoxOfferInvitationCouponSchema" })

export const BoxOfferInvitationInputSchema = z
  .object({
    offerId: z.string(),
    userId: z.string(),
    coupon: BoxOfferInvitationCouponSchema.optional(),
    deferredDate: z.string().optional(),
    subscriptionEndDate: z.string().optional(),
    outOfBand: z.boolean().optional(),
    forceAccept: z.boolean().optional(),
  })
  .meta({ id: "BoxOfferInvitationInputSchema" })

export const BoxOfferInvitationDtoSchema = z
  .object({
    id: z.string(),
    boxOfferId: z.string(),
    boxId: z.string(),
    invitee: UserDtoSchema,
    inviter: UserDtoSchema,
    status: z.enum(BoxOfferInvitationStatus),
    createdAt: z.string(),
    coupon: BoxOfferInvitationCouponSchema.nullable(),
    deferredDate: z.string().nullable(),
    subscriptionEndDate: z.string().nullable(),
    updatedAtDate: z.string().nullable(),
    outOfBand: z.boolean(),
  })
  .meta({ id: "BoxOfferInvitationDtoSchema" })

export const BoxOfferDraftDtoSchema = z
  .object({
    type: z.enum(BoxOfferType),
    name: z.string(),
    description: z.string(),
    maxWeeklyParticipation: z.number().nullable(),
    showcaseDisplay: z.boolean(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    minimumCommitmentPeriod: z.string().optional(),
    commitmentPeriodAutoRenewal: z.boolean().optional(),
    recurringPeriod: IsoDurationSchema.optional(),
    count: z.number().optional(),
    programs: z.array(z.string()),
    rules: z.array(BoxOfferRuleSchema).optional(),
    validityPeriodMonths: z.number().optional(),
    renewable: z.boolean().optional(),
    priceObject: PriceObjectDtoSchema.optional(),
    archived: z.boolean(),
    isFree: z.boolean().optional(),
    currency: z.string().optional(),
  })
  .meta({ id: "BoxOfferDraftDtoSchema" })

export type BoxOfferDraftDto = z.infer<typeof BoxOfferDraftDtoSchema>

export const BoxOfferItemDtoSchema = z
  .object({
    id: z.string(),
    type: z.enum(BoxOfferType),
    name: z.string(),
    description: z.string().nullable(),
    priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
    minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    recurringPeriod: IsoDurationSchema.nullable(),
    count: z.number().nullable(),
    validityPeriodMonths: z.number().nullable(),
    renewable: z.boolean().nullable(),
    showcaseDisplay: z.boolean(), // Show the box offer to users when exploring, default value true
    priceDisplay: z.boolean(), // Show the box offer price to users when exploring, default value true
    autonomousPurchase: z.enum(OfferAutonomousPurchase), // Set who can buy the offer, default value is PUBLIC(anyone with an hustleup account can buy)
    archived: z.boolean(),
    displayOrder: z.number().nullable(),
    /**
     * Deprecated
     */
    price: z.number(),
    currency: z.string().nullable(),
    isFree: z.boolean(),
    rules: z.array(BoxOfferRuleSchema),
    activeMembers: z.number(),
  })
  .meta({ id: "BoxOfferItemDtoSchema" })

export type BoxOfferItemDto = z.infer<typeof BoxOfferItemDtoSchema>

export const BoxOfferDetailedDtoSchema = z
  .object({
    id: z.string(),
    type: z.enum(BoxOfferType),
    name: z.string(),
    description: z.string().nullable(),
    priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
    minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    recurringPeriod: IsoDurationSchema.nullable(),
    count: z.number().nullable(),
    maxWeeklyParticipation: z.number().nullable(),
    validityPeriodMonths: z.number().nullable(), // If null the offer don't have a validity period, it's unlimited
    renewable: z.boolean().nullable(),
    showcaseDisplay: z.boolean(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    archived: z.boolean(),
    displayOrder: z.number().nullable(),
    programs: z.array(z.string()), // Array of program ids
    rules: z.array(BoxOfferRuleSchema),
    /**
     * Deprecated
     */
    currency: z.string().nullable(),
    isFree: z.boolean(),
  })
  .meta({ id: "BoxOfferDetailedDtoSchema" })

// Use for mobile dto for box offer user view in invitation
export const MobileBoxProgramRefDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    image: HUImageDtoSchema.nullable(),
    type: z.enum(ProgramType),
  })
  .meta({ id: "MobileBoxProgramRefDtoSchema" })

export const MobileBoxOfferItemDtoSchema = z
  .object({
    id: z.string(),
    type: z.enum(BoxOfferType),
    name: z.string(),
    description: z.string(),
    priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
    minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    recurringPeriod: IsoDurationSchema.nullable(),
    count: z.number().nullable(),
    validityPeriodMonths: z.number().nullable(), // If null the offer don't have a validity period, it's unlimited
    renewable: z.boolean().nullable(),
    showcaseDisplay: z.boolean(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    canUserPurchase: z.boolean(),
    rules: z.array(BoxOfferRuleSchema), // If null the offer don't have rules
    currency: z.string().nullable(),
  })
  .meta({ id: "MobileBoxOfferItemDtoSchema" })

export const BoxOfferMobileDetailedDtoSchema = z
  .object({
    id: z.string(),
    type: z.enum(BoxOfferType),
    name: z.string(),
    description: z.string().nullable(),
    priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
    minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    recurringPeriod: IsoDurationSchema.nullable(),
    count: z.number().nullable(),
    maxWeeklyParticipation: z.number().nullable(),
    validityPeriodMonths: z.number().nullable(), // If null the offer don't have a validity period, it's unlimited
    renewable: z.boolean().nullable(),
    showcaseDisplay: z.boolean(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    canUserPurchase: z.boolean(),
    programs: MobileBoxProgramRefDtoSchema.array(), // Array of program ids
    rules: z.array(BoxOfferRuleSchema), // If null the offer don't have rules
    hasAlreadyPurchase: z.boolean().nullable(),
    currency: z.string().nullable(),
    invitation: BoxOfferInvitationDtoSchema.nullable(),
  })
  .meta({ id: "BoxOfferMobileDetailedDtoSchema" })

export const BoxSubscriptionOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.Subscription),
}).meta({ id: "BoxSubscriptionOfferSchema" })

export const DropInOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.DropIn),
}).meta({ id: "DropInOfferSchema" })

export const TrialOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.Trial),
  count: z.number(),
  renewable: z.boolean(),
}).meta({ id: "TrialOfferSchema" })

export const TicketsBookOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.TicketsBook),
  count: z.number(),
}).meta({ id: "TicketsBookOfferSchema" })

export const MilleniumOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.Millenium),
  priceObject: z.literal(null),
  isFree: z.literal(true),
}).meta({ id: "MilleniumOfferSchema" })

export type BoxSubscriptionOffer = z.infer<typeof BoxSubscriptionOfferSchema>
export type DropInOffer = z.infer<typeof DropInOfferSchema>
export type TrialOffer = z.infer<typeof TrialOfferSchema>
export type TicketsBookOffer = z.infer<typeof TicketsBookOfferSchema>
export type MilleniumOffer = z.infer<typeof MilleniumOfferSchema>
export type BoxOfferDetailedDto = z.infer<typeof BoxOfferDetailedDtoSchema>
export type BoxOfferInvitationInput = z.infer<typeof BoxOfferInvitationInputSchema>
export type BoxOfferInvitationDto = z.infer<typeof BoxOfferInvitationDtoSchema>
export type MobileBoxOfferItemDto = z.infer<typeof MobileBoxOfferItemDtoSchema>
export type BoxOfferMobileDetailedDto = z.infer<typeof BoxOfferMobileDetailedDtoSchema>
export type MobileBoxProgramRefDto = z.infer<typeof MobileBoxProgramRefDtoSchema>
export type BoxOfferInvitationCoupon = z.infer<typeof BoxOfferInvitationCouponSchema>

export const LightBoxOfferInfoDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    price: PriceObjectDtoSchema.nullable(),
    type: z.enum(BoxOfferType),
    recurringPeriod: IsoDurationSchema.nullable(),
  })
  .meta({ id: "LightBoxOfferInfoDtoSchema" })

export type LightBoxOfferInfoDto = z.infer<typeof LightBoxOfferInfoDtoSchema>
